'use client';

import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { Bot, MousePointerClick, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import TypingAnimation from "./TypingAnimation";

const defaultChatMessages = [
  "O MedFollow pode agilizar sua rotina.",
  "Vamos iniciar um teste?",
];

type InteractiveChatProps = {
    introMessages: string[];
    chatMessages?: string[];
    scrollTriggerId: string;
}

export default function InteractiveChat({ introMessages, chatMessages = defaultChatMessages, scrollTriggerId }: InteractiveChatProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [showButton, setShowButton] = useState(false);
  const [hasAppeared, setHasAppeared] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
        triggerRef.current = document.getElementById(scrollTriggerId);
    }
  }, [scrollTriggerId]);

  const isInView = useInView(triggerRef, { margin: "-200px 0px -200px 0px" });

  useEffect(() => {
    if (isInView && !hasAppeared) {
      const timer = setTimeout(() => {
        setHasAppeared(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isInView, hasAppeared]);

  useEffect(() => {
    if (!isInView && hasAppeared) {
        resetChat();
        setHasAppeared(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView]);

  useEffect(() => {
    if (isInView && !isOpen) {
      setCurrentMessageIndex(Math.floor(Math.random() * introMessages.length));
    }
  }, [isInView, isOpen, introMessages.length])


  const resetChat = () => {
    setIsOpen(false);
    setIsTyping(false);
    setVisibleMessages(0);
    setShowButton(false);
  };

  const handleOpenChat = () => {
    if (isOpen) return;
    setIsOpen(true);
    const timeouts: NodeJS.Timeout[] = [];
    
    let delay = 500;

    timeouts.push(setTimeout(() => setIsTyping(true), delay));

    chatMessages.forEach((_, index) => {
        delay += 1000;
        timeouts.push(setTimeout(() => {
            if (index === 0) setIsTyping(false);
            setVisibleMessages(prev => prev + 1);
        }, delay));
    });

    delay += 700;
    timeouts.push(setTimeout(() => setShowButton(true), delay));

    return () => timeouts.forEach(clearTimeout);
  };

  const messageBubbleVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: "easeOut" } },
  };

  const chatContainerVariants = {
     hidden: { opacity: 0, y: 50, scale: 0.9, originX: 1, originY: 1 },
     visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  }

  const botIconVariants = {
    animate: {
      y: [0, -4, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  const textBubbleVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.8 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: "easeOut" } },
  };


  return (
    <div className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50">
        <AnimatePresence>
            {isInView && hasAppeared && !isOpen && (
                 <motion.div
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={chatContainerVariants}
                    className="flex flex-col items-end gap-2 cursor-pointer"
                    onClick={handleOpenChat}
                 >
                    <motion.div variants={textBubbleVariants} className="bg-background text-foreground text-sm font-medium py-2 px-4 rounded-xl rounded-br-none shadow-lg border">
                      <span>{introMessages[currentMessageIndex]}</span>
                    </motion.div>
                    <motion.div
                      variants={botIconVariants}
                      animate="animate"
                    >
                      <div
                          className="relative h-14 w-14 rounded-full shadow-2xl bg-primary hover:bg-primary/90 flex items-center justify-center"
                      >
                          <Bot className="h-8 w-8 text-primary-foreground" />
                      </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="w-80 h-auto bg-card rounded-2xl shadow-2xl border border-border flex flex-col"
          >
            <div className="p-4 border-b flex items-center gap-3 bg-secondary/50 rounded-t-2xl relative">
              <Bot className="h-7 w-7 text-primary" />
              <div>
                <h3 className="font-bold text-foreground">MedFollow Assistant</h3>
                <p className="text-xs text-foreground/70">Online</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8"
                onClick={resetChat}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Fechar chat</span>
              </Button>
            </div>
            <div className="flex-1 p-4 min-h-[150px] flex flex-col gap-3 bg-secondary/20">
              {chatMessages.slice(0, visibleMessages).map((msg, i) => (
                <motion.div
                  key={i}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={messageBubbleVariants}
                  className={"w-fit max-w-[90%] rounded-xl px-3 py-2 bg-primary text-primary-foreground self-start"}
                >
                  <p className="text-sm">{msg}</p>
                </motion.div>
              ))}
              {isTyping && <TypingAnimation />}
              {showButton && (
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={messageBubbleVariants}
                    className="self-center pt-2"
                  >
                    <Button onClick={resetChat} className="bg-green-300 text-black hover:bg-green-400">
                        <MousePointerClick className="mr-2 h-4 w-4"/>
                        Iniciar meu teste
                    </Button>
                  </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
